import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BusyOverlay from '../commonComponents/BusyOverlay';
import DataTable from '../data_table/DataTable';
import KitTooltip from '../ui/Tooltip';
import { optimizerGroupIntentKey } from '../../utils/optimizerGenericGroupContract';
import { isAssignedAttributeGroup } from '../../utils/watchlistAttributePresentation';
import LimitTrack from './LimitTrack';
import RuleLimitInput from './RuleLimitInput';
import RuleStatusChip from './RuleStatusChip';
import {
  AttachedLimit,
  EmptyScopeMessage,
  NotSet,
  RULE_TABLE_CLASS_NAME,
  RULE_TABLE_HEADER_HEIGHT,
  RuleValue,
  renderRuleFullWidthRow,
  ruleColumns,
  ruleModeControl,
  ruleSwitchedOff,
} from './OptimizerRuleTableParts';
import {
  ATTRIBUTE_RULE_METRICS,
  formatRuleValue,
  ruleEditState,
} from './optimizerRuleRegistry';

const VOLATILITY_DESCRIPTION = "Volatility of the category's positions in USD, with the factor part in the "
  + 'tooltip. Read-only; attribute limits do not constrain it.';

// A metric's section header: a full-width row tinted with the accent, with
// an accent edge on the left.
const SECTION_ROW_CLASS_NAME = '!bg-accent/[0.055] dark:!bg-accent/[0.14] '
  + '[&>td]:shadow-[inset_3px_0_rgb(var(--c-accent))]';
const getRowClassName = (row) => (row.section ? SECTION_ROW_CLASS_NAME : undefined);

// One pill per attribute; the selected attribute is filled. A small "N on"
// badge counts the limits switched on in that attribute.
const attributeTabsSx = {
  flex: 1,
  minWidth: 0,
  minHeight: 34,
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTabs-flexContainer': { gap: 0.75, alignItems: 'center', py: 0.25 },
  '& .MuiTab-root': {
    minHeight: 30,
    height: 30,
    px: 1.5,
    py: 0,
    border: 1,
    borderColor: 'divider',
    borderRadius: '999px',
    textTransform: 'none',
    fontSize: '0.76rem',
    fontWeight: 650,
    letterSpacing: 0,
    color: 'text.secondary',
    bgcolor: 'background.paper',
    transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
    '&:hover': {
      color: 'text.primary',
      borderColor: (theme) => alpha(theme.palette.primary.main, 0.45),
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
    },
  },
  '& .MuiTab-root.Mui-selected': {
    color: 'primary.contrastText',
    bgcolor: 'primary.main',
    borderColor: 'primary.main',
    boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  '& .attribute-tab-count': {
    ml: 0.75,
    px: 0.75,
    borderRadius: '999px',
    fontSize: '0.64rem',
    fontWeight: 700,
    lineHeight: '16px',
    color: 'primary.main',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
  },
  '& .Mui-selected .attribute-tab-count': {
    color: 'primary.contrastText',
    bgcolor: (theme) => alpha(theme.palette.common.white, 0.22),
  },
};

const assignedGroups = (definition) => (
  (definition?.groups || []).filter(isAssignedAttributeGroup)
);

const intentFor = (definitionId, group, metricName) => ({
  attribute_definition_id: definitionId,
  group_kind: group.group_kind,
  group_value_key: group.group_value_key ?? null,
  name: metricName,
});

const lowerFirst = (text) => `${text.charAt(0).toLowerCase()}${text.slice(1)}`;

// Attributes scope: category limits are attached, edited and switched off in the
// row that shows the constrained group exposure. Every metric is laid out in its
// own section (beta exposure first), so no limit hides behind a toggle. Group
// volatility is read-only context from the same receipt.
export default function AttributeConstraintCard({
  attributeDefinitions = [],
  attributeRiskMetrics = null,
  groupConstraints,
  evaluationIndex,
  baselineLabel,
  actions,
  recordLimit,
  resultStale = false,
  disabled,
  metadataReady,
  riskLoading = false,
  searchRanges = null,
  rangeEditor = null,
  modeEditor = null,
}) {
  const populatedDefinitions = useMemo(() => (
    (attributeDefinitions || []).filter((definition) => assignedGroups(definition).length > 0)
  ), [attributeDefinitions]);
  const [activeDefinitionId, setActiveDefinitionId] = useState('');

  useEffect(() => {
    if (!populatedDefinitions.some(({ attribute_definition_id: id }) => id === activeDefinitionId)) {
      setActiveDefinitionId(populatedDefinitions[0]?.attribute_definition_id || '');
    }
  }, [activeDefinitionId, populatedDefinitions]);

  const resolvedActiveDefinitionId = populatedDefinitions.some(
    ({ attribute_definition_id: id }) => id === activeDefinitionId,
  )
    ? activeDefinitionId
    : populatedDefinitions[0]?.attribute_definition_id || '';
  const activeDefinition = populatedDefinitions.find(
    ({ attribute_definition_id: id }) => id === resolvedActiveDefinitionId,
  );
  // The record limit is enforced here; it is too large to be worth showing.
  const atLimit = groupConstraints.length >= recordLimit;
  const groupRiskByDefinition = useMemo(() => {
    const indexPayload = (payload) => new Map(
      (payload?.attribute_results || []).map((result) => [
        String(result?.attribute_definition_id || ''),
        new Map((result?.groups || []).map((group) => [
          `${group?.group?.group_kind || ''}:${group?.group?.group_value_key || ''}`,
          group?.metrics || {},
        ])),
      ]),
    );
    return {
      actual: indexPayload(attributeRiskMetrics?.actual),
      optimized: indexPayload(attributeRiskMetrics?.optimized),
    };
  }, [attributeRiskMetrics]);

  const groupRisk = (side, group) => (
    groupRiskByDefinition[side]
      .get(activeDefinition?.attribute_definition_id)
      ?.get(`${group.group_kind}:${group.group_value_key || ''}`) ?? null
  );
  const riskTooltip = (metrics) => (
    metrics?.factor_vol == null
      ? null
      : `Factor volatility ${formatRuleValue(metrics.factor_vol, 'usd')}`
  );
  const enabledCountForDefinition = (definitionId) => groupConstraints.filter((record) => (
    record.attribute_definition_id === definitionId && record.enabled === true
    && isAssignedAttributeGroup(record)
  )).length;

  const hasModeEditor = Boolean(modeEditor);
  const hasRangeEditor = Boolean(rangeEditor);
  const columns = useMemo(() => {
    const [category, mode, limit, baseline, optimized, track, status, volatilityBaseline, volatilityOptimized] = ruleColumns([
      { id: 'category', header: 'Category', width: 14, wrap: false },
      { id: 'mode', header: hasModeEditor ? 'Mode' : 'On', width: hasModeEditor ? 9 : 6, align: 'center' },
      {
        id: 'limit',
        header: hasRangeEditor ? 'Limit / range' : 'Limit',
        width: hasRangeEditor ? 20 : 14,
        align: 'right',
      },
      { id: 'baseline', header: baselineLabel, width: 9, align: 'right', wrap: false },
      { id: 'optimized', header: 'Optimized', width: 9, align: 'right', wrap: false, optimized: true },
      { id: 'track', header: 'Result vs limit', width: 15, align: 'right' },
      { id: 'status', header: 'Status', width: 14 },
      { id: 'volatilityBaseline', header: baselineLabel, width: 9.5, align: 'right', wrap: false },
      {
        id: 'volatilityOptimized',
        header: 'Optimized',
        width: 9.5,
        align: 'right',
        wrap: false,
        optimized: true,
      },
    ], hasRangeEditor ? 1120 : 920);
    return [
      category,
      mode,
      limit,
      { id: 'groupExposure', header: 'Group exposure', columns: [baseline, optimized, track, status] },
      {
        id: 'groupVolatility',
        header: 'Group total volatility ($)',
        meta: { description: VOLATILITY_DESCRIPTION },
        columns: [volatilityBaseline, volatilityOptimized],
      },
    ];
  }, [baselineLabel, hasModeEditor, hasRangeEditor]);

  if (!metadataReady) {
    return (
      <EmptyScopeMessage>
        Attribute constraints will appear when the optimizer context is ready.
      </EmptyScopeMessage>
    );
  }
  if (populatedDefinitions.length === 0) {
    return (
      <EmptyScopeMessage>
        This Watchlist has no populated attribute categories to constrain.
      </EmptyScopeMessage>
    );
  }

  const groupRow = (metric, group) => {
    const intent = intentFor(
      activeDefinition.attribute_definition_id,
      group,
      metric.name,
    );
    const intentKey = optimizerGroupIntentKey(intent);
    const record = groupConstraints.find((candidate) => (
      optimizerGroupIntentKey(candidate) === intentKey
    )) ?? null;
    const evaluation = evaluationIndex.byGroupIntentKey.get(intentKey);
    const categoryLabel = group.group_value;
    // Each category appears once per metric, so labels name both.
    const ruleLabel = `${categoryLabel} ${lowerFirst(metric.label)}`;
    const actualRisk = groupRisk('actual', group);
    const optimizedRisk = groupRisk('optimized', group);
    const cannotAttach = !record && atLimit;
    const parameter = evaluation ? `group:${evaluation.rule_id}` : null;
    const { changed, draftLimit } = ruleEditState(
      evaluation,
      record ? { enabled: record.enabled, value: record.value, explore: Boolean(searchRanges?.[`group:${evaluation?.rule_id}`]) } : null,
    );
    const limitEditor = () => (rangeEditor ? rangeEditor({ parameter,
      value: record.value, unit: metric.unit, form: metric.form, label: ruleLabel, disabled,
      onChange: (value) => actions.setGroupRuleValue(intentKey, value),
    }) : (
      <RuleLimitInput
        value={record.value ?? null}
        onChange={(value) => actions.setGroupRuleValue(intentKey, value)}
        unit={metric.unit}
        form={metric.form}
        disabled={disabled}
        ariaLabel={`${ruleLabel} limit`}
        width={112}
      />
    ));
    return {
      id: `${metric.name}:${group.group_kind}:${group.group_value_key || ''}`,
      cells: {
        category: (
          <KitTooltip content={categoryLabel}>
            <span className="block overflow-hidden text-ellipsis text-[0.76rem]">{categoryLabel}</span>
          </KitTooltip>
        ),
        // A disabled switch takes no pointer events, so the tooltip sits on
        // a wrapper.
        mode: (
          <KitTooltip content={cannotAttach ? 'Attribute constraint limit reached' : null}>
            <span className="inline-flex">
              {ruleModeControl(modeEditor, {
                parameter,
                enabled: record?.enabled === true,
                value: record?.value ?? 10,
                label: ruleLabel,
                disabled: disabled || cannotAttach,
                onEnabledChange: (value) => actions.setGroupRuleEnabled({
                  attributeDefinitionId: activeDefinition.attribute_definition_id,
                  group,
                  metricName: metric.name,
                }, value),
              })}
            </span>
          </KitTooltip>
        ),
        limit: record?.enabled === true ? (
          <AttachedLimit
            editor={limitEditor()}
            label={ruleLabel}
            onRemove={() => actions.removeGroupRule(intentKey)}
            disabled={disabled}
          />
        ) : <NotSet />,
        baseline: (
          <RuleValue
            value={evaluation?.baseline ?? null}
            unit={metric.unit}
            muted={ruleSwitchedOff(evaluation)}
            tooltip={evaluation ? formatRuleValue(evaluation.detail?.baseline_usd, 'usd') : null}
          />
        ),
        optimized: (
          <RuleValue
            value={evaluation?.optimized ?? null}
            unit={metric.unit}
            muted={ruleSwitchedOff(evaluation)}
            tooltip={evaluation ? formatRuleValue(evaluation.detail?.optimized_usd, 'usd') : null}
          />
        ),
        track: (
          <LimitTrack
            evaluation={evaluation}
            rule={metric}
            label={ruleLabel}
            baselineLabel={baselineLabel}
            draftLimit={draftLimit}
          />
        ),
        status: (
          <RuleStatusChip
            evaluation={evaluation}
            rule={metric}
            changed={changed}
            stale={resultStale}
          />
        ),
        volatilityBaseline: (
          <RuleValue
            value={actualRisk?.total_vol ?? null}
            unit="usd"
            tooltip={riskTooltip(actualRisk)}
          />
        ),
        volatilityOptimized: (
          <RuleValue
            value={optimizedRisk?.total_vol ?? null}
            unit="usd"
            tooltip={riskTooltip(optimizedRisk)}
          />
        ),
      },
    };
  };

  // Each metric is a section: a full-width header row, then its categories.
  const rows = ATTRIBUTE_RULE_METRICS.flatMap((metric) => [
    {
      id: `section:${metric.name}`,
      section: true,
      fullWidth: (
        <span className="block pl-[5px]">
          <span className="text-[0.74rem] font-extrabold">{metric.label}</span>
          <span className="ml-1.5 text-[0.68rem] text-content-secondary">
            {`${metric.form === 'abs_max' ? '± limit as ' : 'limit as '}${metric.unitLabel}`}
          </span>
        </span>
      ),
    },
    ...assignedGroups(activeDefinition).map((group) => groupRow(metric, group)),
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, minWidth: 0 }}>
        <Tabs
          value={resolvedActiveDefinitionId}
          onChange={(_event, value) => setActiveDefinitionId(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Watchlist attribute constraints"
          sx={attributeTabsSx}
        >
          {populatedDefinitions.map((definition) => {
            const count = enabledCountForDefinition(definition.attribute_definition_id);
            const countText = `${count} limit${count === 1 ? '' : 's'} switched on`;
            return (
              <Tab
                key={definition.attribute_definition_id}
                value={definition.attribute_definition_id}
                aria-label={count
                  ? `${definition.display_name}, ${countText}`
                  : definition.display_name}
                label={(
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    {definition.display_name}
                    {count > 0 && (
                      <Tooltip title={countText} arrow>
                        <Box component="span" className="attribute-tab-count">
                          {`${count} on`}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                )}
              />
            );
          })}
        </Tabs>
      </Box>

      <BusyOverlay show={riskLoading}>
        <DataTable
          aria-label={`${activeDefinition.display_name} optimizer constraints`}
          data={rows}
          columns={columns}
          renderFullWidthRow={renderRuleFullWidthRow}
          getRowClassName={getRowClassName}
          headerHeight={RULE_TABLE_HEADER_HEIGHT}
          wrapHeaders
          className={RULE_TABLE_CLASS_NAME}
        />
      </BusyOverlay>
    </Box>
  );
}

AttributeConstraintCard.propTypes = {
  attributeDefinitions: PropTypes.arrayOf(PropTypes.shape({
    attribute_definition_id: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
    groups: PropTypes.arrayOf(PropTypes.shape({
      group_kind: PropTypes.string.isRequired,
      group_value: PropTypes.string,
      group_value_key: PropTypes.string,
    })).isRequired,
  })),
  attributeRiskMetrics: PropTypes.object,
  groupConstraints: PropTypes.arrayOf(PropTypes.shape({
    attribute_definition_id: PropTypes.string.isRequired,
    group_kind: PropTypes.string.isRequired,
    group_value_key: PropTypes.string,
    name: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    value: PropTypes.number,
  })).isRequired,
  evaluationIndex: PropTypes.shape({
    byGroupIntentKey: PropTypes.instanceOf(Map),
  }).isRequired,
  baselineLabel: PropTypes.string.isRequired,
  actions: PropTypes.object.isRequired,
  recordLimit: PropTypes.number.isRequired,
  resultStale: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
  metadataReady: PropTypes.bool.isRequired,
  riskLoading: PropTypes.bool,
};
